import type { StacCollection, StacProvider } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';

/**
 * STAC keeps credit in `providers`. The `licensor` role names who licenses the data,
 * `description` holds the wording they ask for, and `url` their homepage. A field of our
 * own is invisible to every other client of this catalogue.
 */

/** Who produced the data is a different question from how it is licensed. */
export const NATUREMETRICS_PROVIDER: StacProvider = {
    name: 'NatureMetrics',
    roles: ['producer', 'licensor'],
};

export const isOurProvider = (provider: StacProvider): boolean =>
    provider.name === NATUREMETRICS_PROVIDER.name && (provider.roles ?? []).includes('producer');

/** Our own entry also holds the `licensor` role, so that role alone is not a credit. */
export const isCreditedProvider = (provider: StacProvider): boolean =>
    !isOurProvider(provider) && (provider.roles ?? []).includes('licensor');

export type CatalogueAttribution = {
    /** Absent where the catalogue credits nobody. */
    creditedTo?: string;
    /** The wording the licensor asks for. Absent where they ask for none. */
    credit?: string;
    url?: string;
    /** An SPDX identifier, `proprietary`, or `various`. STAC permits no other value. */
    license: string;
};

const trimmed = (value?: string): string | undefined => {
    const text = (value ?? '').trim();
    return text.length > 0 ? text : undefined;
};

/** The result is null before the collection is read. A dataset that credits nobody still
 *  states a licence. */
export const attributionOf = (collection: StacCollection | undefined): CatalogueAttribution | null => {
    if (!collection) return null;

    const credited = (collection.providers ?? []).filter(isCreditedProvider).find(provider => trimmed(provider.name));

    return {
        ...(credited ? { creditedTo: trimmed(credited.name) } : {}),
        ...(credited && trimmed(credited.description) ? { credit: trimmed(credited.description) } : {}),
        ...(credited && trimmed(credited.url) ? { url: trimmed(credited.url) } : {}),
        license: collection.license,
    };
};
